import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useGalleryState } from '../hooks/useGalleryState';
import { useUserInfo } from '../../Authentication/hooks';
import { deleteAsset, restoreAsset, updateAssetName } from '../../../assets/requests';
import { toast } from 'react-toastify';
import upArrow from '../../../assets/images/up-arrow.png'
import backArrow from '../../../assets/images/back-arrow3.png'
import homeIcon from '../../../assets/images/home-icon.png'
import deleteImg from '../../../assets/images/delete2.svg'
import {createNavigationHandlers} from './utils/categoryUtils'
import '../../../assets/stylesheets/duplicates.css'
import '../../../assets/stylesheets/gallery-nav.css'
import '../../../assets/stylesheets/gallery-style.css'
import '../../../assets/stylesheets/documents.css'

const DocumentCard = ({ doc, onOpen, onDelete, onRename }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(doc.name);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleEditClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setEditName(doc.name);
    setIsEditing(true);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onDelete) onDelete(doc);
  };

  const handleSave = () => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== doc.name && onRename) {
      onRename(doc, trimmed);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); handleSave(); }
    else if (e.key === 'Escape') { e.preventDefault(); setEditName(doc.name); setIsEditing(false); }
  };

  return (
    <div className="doc-thumb-card" onClick={() => onOpen(doc)}>
      <div className="doc-thumb-image-wrap">
        <img
          src={doc.thumbnailUrl || ''}
          alt={doc.name || 'Document'}
          className="doc-thumb-image"
        />
        <button
          className="category-edit-icon"
          onClick={handleEditClick}
          title="Rename document"
        >
          ✎
        </button>
        <button
          className="category-delete-icon"
          onClick={handleDeleteClick}
          title="Delete document"
        >
          <img src={deleteImg} alt="Delete" style={{ width: '16px', height: '16px' }} />
        </button>
      </div>
      <div className="doc-thumb-details">
        {isEditing ? (
          <input
            ref={inputRef}
            className="thumb-title-input"
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <div className="doc-thumb-title">{doc.name}</div>
        )}
        {doc.description && (
          <div className="doc-thumb-desc">{doc.description}</div>
        )}
      </div>
    </div>
  );
};

const YearDocuments = () => {
  const { year: rawYear } = useParams();
  const year = (rawYear || '').toString().trim();
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useUserInfo();
  const { state, combinedNavigation } = useGalleryState();
  const [deletedDocIds, setDeletedDocIds] = useState(new Set());
  const { handleHomeClick, handleBackClick, handleBackToTop } =
    createNavigationHandlers(navigate)

  const normalizeDoc = d => ({
    id: d.id,
    name: d.origFilename || d.name || d.filename || 'Untitled',
    fileUrl: d.fileUrl || d.file_url || d.s3uri || d.fileUrlSigned || '',
    thumbnailUrl: d.thumbnailUrl || d.thumbnail_url || d.thumbnail || d.thumbnailUrlSigned || '',
    description: d.description || ''
  });

  // Resolving the year node either from navigation state or strictly from the Documents superelm
  const resolvedYearNode = useMemo(() => {
    const fromLocation = location.state?.yearNode;
    if (fromLocation) return fromLocation;

    if (!combinedNavigation || combinedNavigation.length === 0) return null;

    const documentsSuper = combinedNavigation.find(n => String(n.name).trim() === 'Documents');
    if (!documentsSuper || !Array.isArray(documentsSuper.subCategories)) return null;

    const found = documentsSuper.subCategories.find(y => String(y.name).trim() === year);
    if (found) return found;

    return null;
  }, [location.state, combinedNavigation, year]);

  const documentsFromLocation = useMemo(() => {
    const yearNode = location.state?.yearNode;
    if (!yearNode || !Array.isArray(yearNode.subCategories)) return [];
    return yearNode.subCategories.map(normalizeDoc);
  }, [location.state]);

  // Primary source: state.documents (fetched documents)
  const documentsFromState = useMemo(
    () =>
      (state.documents || []).filter(doc => {
        const yearCat = (doc.categories || []).find(
          c => c && c.type === 'year' && c.name
        );
        return String(yearCat?.name).trim() === year;
      }).map(normalizeDoc),
    [state.documents, year]
  );

  const documentsFromNav = useMemo(() => {
    if (!combinedNavigation || combinedNavigation.length === 0) return [];

    const documentsSuper = combinedNavigation.find(n => String(n.name).trim() === 'Documents');
    if (!documentsSuper || !Array.isArray(documentsSuper.subCategories)) return [];

    const yearNode = documentsSuper.subCategories.find(y => String(y.name).trim() === year);
    if (!yearNode || !Array.isArray(yearNode.subCategories)) return [];

    return yearNode.subCategories.map(normalizeDoc);
  }, [combinedNavigation, year]);

  const documentsForYearRaw = documentsFromLocation.length > 0 ? documentsFromLocation : (documentsFromState.length > 0 ? documentsFromState : documentsFromNav);
  const [renamedDocs, setRenamedDocs] = useState({});

  const documentsForYear = useMemo(() =>
    documentsForYearRaw
      .filter(doc => !deletedDocIds.has(doc.id))
      .map(doc => renamedDocs[doc.id] ? { ...doc, name: renamedDocs[doc.id] } : doc),
    [documentsForYearRaw, deletedDocIds, renamedDocs]
  );

  const subcategoryTitle = useMemo(() => {
    if (resolvedYearNode && resolvedYearNode.name && String(resolvedYearNode.name).trim() !== '') {
      const name = String(resolvedYearNode.name).trim();
      if (name !== year) return name;
    }

    const docs = (state.documents || []).filter(doc => {
      const yearCat = (doc.categories || []).find(c => c && c.type === 'year' && c.name);
      return String(yearCat?.name).trim() === year;
    });

    if (docs.length) {
      const first = docs[0];
      const subcat = (first.categories || []).find(c => c && c.type && c.type !== 'year' && c.name);
      if (subcat && subcat.name) return String(subcat.name).trim();
    }

    return year;
  }, [resolvedYearNode, state.documents, year]);

  const openDocument = useCallback(
    doc => {
      if (!doc) return;
      const url = doc.fileUrl || '';
      if (url) {
        window.open(url, '_blank');
      }
    },
    []
  );

  const handleDeleteDocument = useCallback(
    async (doc) => {
      try {
        console.log('Deleting document:', { userId, assetId: doc.id, docName: doc.name });
        const result = await deleteAsset({ userId, assetId: doc.id });
        console.log('Delete result:', result);
        // Remove from view immediately
        setDeletedDocIds(prev => new Set(prev).add(doc.id));

        const handleUndo = async () => {
          try {
            await restoreAsset({ userId, assetId: doc.id });
            // Re-add to view
            setDeletedDocIds(prev => {
              const next = new Set(prev);
              next.delete(doc.id);
              return next;
            });
            toast.success('Document restored!', { autoClose: 1000, hideProgressBar: true });
          } catch (err) {
            console.error('Failed to restore document:', err);
            toast.error('Failed to restore document.', { autoClose: 2000, hideProgressBar: true });
          }
        };

        toast.info(
          ({ closeToast }) => (
            <div>
              Document &quot;{doc.name}&quot; deleted.{' '}
              <button
                onClick={() => { handleUndo(); closeToast(); }}
                style={{ background: 'none', border: 'none', color: '#3498db', cursor: 'pointer', textDecoration: 'underline', fontSize: 'inherit', padding: 0 }}
              >
                Undo
              </button>
            </div>
          ),
          { autoClose: 5000, hideProgressBar: false }
        );
      } catch (error) {
        console.error('Error deleting document:', error);
        toast.error('Failed to delete document.', { autoClose: 2000, hideProgressBar: true });
      }
    },
    [userId]
  );

  const handleRenameDocument = useCallback(
    async (doc, newName) => {
      try {
        await updateAssetName({ userId, assetId: doc.id, name: newName });
        // Update name in local view
        setRenamedDocs(prev => ({ ...prev, [doc.id]: newName }));
      } catch (error) {
        console.error('Error renaming document:', error);
        toast.error('Failed to rename document.', { autoClose: 2000, hideProgressBar: true });
      }
    },
    [userId]
  );

  return (
    <div className="year-documents-page" style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, marginTop:110}}>
      </div>
      <div className="go-back">
          <img
            className="nav-icon-home home fav"
            alt="Go to base of navigation"
            src={homeIcon}
            onClick={handleHomeClick}
          />
          <img
            className="nav-icon-back back"
            alt="Go back one level."
            src={backArrow}
            onClick={handleBackClick}
          />
          <img
            className="nav-icon-top top"
            alt="Go back to top."
            src={upArrow}
            onClick={handleBackToTop}
          />
        </div>
      <h2 style={{ margin: 0 }}>Documents — {subcategoryTitle}</h2>
      {documentsForYear.length === 0 ? (
        <p>No documents found for {year}.</p>
      ) : (
        <div className="documents-grid">
          {documentsForYear.map(doc => (
            <DocumentCard
              key={doc.id}
              doc={doc}
              onOpen={openDocument}
              onDelete={handleDeleteDocument}
              onRename={handleRenameDocument}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default YearDocuments;
